import { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import { toast } from 'sonner'
import { FolderOpen, FolderPlus, MoreHorizontal, Pencil, Trash2 } from 'lucide-react'

import { ChainlitContext, useAuth } from '@chainlit/react-client'
import { Loader } from '@/components/Loader'

import Page from 'pages/Page'
import { useTranslation } from '@/components/i18n/Translator'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { foldersState } from '@/state/folders'
import type { Folder } from '@/api'

export default function Projects() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const apiClient = useContext(ChainlitContext) as any
  const [folders, setFolders] = useRecoilState(foldersState)
  const [loading, setLoading] = useState(true)

  const [createOpen, setCreateOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const renameInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (user !== undefined && !user) navigate('/', { replace: true })
  }, [user, navigate])

  useEffect(() => {
    if (!apiClient?.listFolders) return
    apiClient.listFolders().then((f: Folder[]) => {
      setFolders(f)
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (renamingId) setTimeout(() => renameInputRef.current?.focus(), 50)
  }, [renamingId])

  const handleCreate = async () => {
    if (!newName.trim()) return
    try {
      const folder = await apiClient.createFolder(newName.trim())
      setFolders(prev => [...prev, folder])
      setCreateOpen(false)
      setNewName('')
      navigate(`/project/${folder.id}`)
    } catch {
      toast.error(t('projects.errors.create'))
    }
  }

  const handleRename = async (id: string) => {
    const trimmed = renameValue.trim()
    if (!trimmed) { setRenamingId(null); return }
    try {
      await apiClient.renameFolder(id, trimmed)
      setFolders(prev => prev.map(f => f.id === id ? { ...f, name: trimmed } : f))
    } catch {
      toast.error(t('projects.errors.rename'))
    } finally {
      setRenamingId(null)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await apiClient.deleteFolder(id)
      setFolders(prev => prev.filter(f => f.id !== id))
    } catch {
      toast.error(t('projects.errors.delete'))
    }
  }

  if (user === undefined) return null

  return (
    <Page>
      <div className="flex flex-col w-full h-full overflow-auto px-6 py-8">
        <div className="w-full max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-semibold">Mina projekt</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Organisera dina konversationer i projekt
              </p>
            </div>
            <Button onClick={() => setCreateOpen(true)} className="gap-2">
              <FolderPlus className="h-4 w-4" />
              Nytt projekt
            </Button>
          </div>

          {/* Grid */}
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader className="h-6 w-6" />
            </div>
          ) : folders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
              <div className="h-16 w-16 rounded-2xl border-2 border-dashed border-border flex items-center justify-center">
                <FolderOpen className="h-7 w-7 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm font-medium">Inga projekt ännu</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Skapa ett projekt för att organisera dina konversationer.
                </p>
              </div>
              <Button variant="outline" onClick={() => setCreateOpen(true)} className="gap-2 mt-2">
                <FolderPlus className="h-4 w-4" />
                Skapa första projekt
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {folders.map(folder => (
                <div
                  key={folder.id}
                  className="group relative flex flex-col gap-3 p-5 rounded-xl border border-border bg-card hover:bg-accent/40 hover:border-accent transition-colors cursor-pointer"
                  onClick={() => navigate(`/project/${folder.id}`)}
                >
                  {/* Actions dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="absolute top-3 right-3 h-7 w-7 flex items-center justify-center rounded-md opacity-0 group-hover:opacity-100 hover:bg-accent transition-all"
                        onClick={e => e.stopPropagation()}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={e => e.stopPropagation()}>
                      <DropdownMenuItem onClick={e => {
                        e.stopPropagation()
                        setRenamingId(folder.id)
                        setRenameValue(folder.name)
                      }}>
                        <Pencil className="mr-2 h-4 w-4" />
                        {t('projects.actions.rename')}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={e => { e.stopPropagation(); handleDelete(folder.id) }}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {t('projects.actions.delete')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <FolderOpen className="h-8 w-8 text-muted-foreground" />

                  {/* Name — inline rename */}
                  {renamingId === folder.id ? (
                    <Input
                      ref={renameInputRef}
                      value={renameValue}
                      className="h-7 text-sm font-medium px-1"
                      onChange={e => setRenameValue(e.target.value)}
                      onBlur={() => handleRename(folder.id)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleRename(folder.id)
                        if (e.key === 'Escape') setRenamingId(null)
                        e.stopPropagation()
                      }}
                      onClick={e => e.stopPropagation()}
                    />
                  ) : (
                    <p className="text-sm font-medium truncate pr-6">{folder.name}</p>
                  )}

                  <p className="text-xs text-muted-foreground">
                    {new Date(folder.createdAt).toLocaleDateString('sv-SE', {
                      year: 'numeric', month: 'short', day: 'numeric',
                    })}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('projects.dialogs.create.title')}</DialogTitle>
          </DialogHeader>
          <Input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder={t('projects.dialogs.create.placeholder')}
            autoFocus
            onKeyDown={e => e.key === 'Enter' && handleCreate()}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              {t('common.actions.cancel')}
            </Button>
            <Button onClick={handleCreate}>
              {t('common.actions.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  )
}
